import React, { useMemo, useState, useEffect } from "react";
import Table from "../../../../components/table/Table";
import { useQuery, useMutation } from "react-query";
import { api as coursesApi } from "../../../../api/courses";
import { convertDate } from "../../../helpers";
import CenteredModal from "../../../../components/Modal/CenteredModal";
import AddCategory from "./components/AddCategory";
import toast from "react-hot-toast";
import Pagination from "../../../../components/pagination/Pagination";
import Confirmation from "../../components/ConfimationModal";
import PermissionGate from "../../../../components/PermissionGate";

const Categories = () => {
  const [openModal, setOpenModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [deleteModal, setDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Debounce: fire API 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, refetch } = useQuery(
    ["CourseCategories", currentPage, perPage, search],
    () =>
      coursesApi.getCategories({
        perPage,
        currentPage,
        search: search || undefined,
        // sortBy and order are omitted — only pass them when user explicitly sorts
      }),
    {
      cacheTime: 10000,
      staleTime: 10000,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      select: (data) => data,
    }
  );

  const deleteMutation = useMutation(coursesApi.deleteCategory);

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    try {
      toast.loading("Deleting...");
      const res = await deleteMutation.mutateAsync(categoryToDelete.id);
      toast.remove();
      toast.success(res?.message || "Category deleted successfully");
      setDeleteModal(false);
      setCategoryToDelete(null);
      refetch();
    } catch (error) {
      toast.remove();
      toast.error(error?.response?.data?.message || "Failed to delete");
    }
  };

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setDeleteModal(true);
  };

  const handleEdit = (category) => {
    setCategoryToEdit(category);
    setOpenModal(true);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearch("");
    setCurrentPage(1);
  };

  let columns = useMemo(
    () => [
      {
        title: "ID",
        selector: null,
        key: "id",
        render: ({ index }) =>
          index + 1 + ((data?.meta?.page || 1) - 1) * (data?.meta?.perPage || perPage),
      },
      {
        title: "Name",
        selector: "name",
        key: "name",
        render: ({ item }) => <span className="font-semibold text-gray-800">{item}</span>,
      },
      {
        title: "Description",
        selector: "description",
        key: "description",
      },
      {
        title: "Created At",
        selector: "createdAt",
        key: "createdAt",
        render: ({ item }) => <span>{convertDate(item)}</span>,
      },
      {
        title: "Action",
        selector: "id",
        key: "id",
        render: ({ row }) => (
          <div className="flex items-center gap-3">
            <PermissionGate slug="category-management.update">
              <button type="button" onClick={() => handleEdit(row)} title="Edit">
                <i className="bi bi-pencil-square text-primary-blue text-lg"></i>
              </button>
            </PermissionGate>
            <PermissionGate slug="category-management.delete">
              <button type="button" onClick={() => confirmDelete(row)} title="Delete">
                <i className="bi bi-trash text-red-500 text-lg"></i>
              </button>
            </PermissionGate>
          </div>
        ),
      },
    ],
    [data]
  );

  return (
    <div className="p-5">
      <header className="flex justify-between items-center mt-1 mb-5">
        <h5 className="text-md font-semibold">
          Total Categories ({data?.meta?.total ?? 0})
        </h5>
        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              className="input-style bg-primaryGray w-[300px] pl-8"
              placeholder="Search categories..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <PermissionGate slug="category-management.create">
          <button
            onClick={() => {
              setCategoryToEdit(null);
              setOpenModal(true);
            }}
            className="btn pt-3 pb-3 pl-8 pr-8 bg-primary-blue ml-2"
          >
            Create Category
          </button>
          </PermissionGate>
        </div>
      </header>

      <div className="p-5 bg-white rounded-xl">
        <Table data={data?.data} isLoading={isLoading} columns={columns} />
        <Pagination
          data={data?.meta}
          perPage={perPage}
          pageNo={currentPage}
          setPageNo={setCurrentPage}
          setPerPage={setPerPage}
        />
      </div>

      <CenteredModal
        open={openModal}
        setOpen={setOpenModal}
        title={categoryToEdit ? "Edit Category" : "Create New Category"}
        onClose={() => setOpenModal(false)}
        width={"w-[500px]"}
      >
        <AddCategory
          categoryToEdit={categoryToEdit}
          refetch={refetch}
          handleModal={() => setOpenModal(false)}
        />
      </CenteredModal>

      <Confirmation
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        title="Delete Category"
        text={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
        width="w-[450px]"
        footerButtonsAlignment="end"
        footer={
          <>
            <button
              className="btn-light pl-8 pr-8 bg-primaryGray mr-3"
              onClick={() => { setDeleteModal(false); setCategoryToDelete(null); }}
            >
              Cancel
            </button>
            <button
              className="btn pl-8 pr-8 bg-red-500"
              onClick={handleDelete}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? "Deleting..." : "Delete"}
            </button>
          </>
        }
      />
    </div>
  );
};

export default Categories;

